/* eslint-disable no-param-reassign */
/* eslint-disable no-restricted-globals */
import { Device, DeviceInfo } from '@capacitor/device';
import {
  useAppleExternalSignIn,
  useFacebookExternalSignIn,
  useGoogleExternalSignIn,
  useRegisterUser,
} from 'app/core/api/auth/api-auth';
import {
  appleSignIn,
  facebookSignIn,
  googleSignIn,
  initializeFacebookSignIn,
} from 'app/core/api/auth/external-sign-in';
import { appEnv } from 'app/core/environment';
import { useAuthToken } from 'app/core/token/token-utils';
import AppImage from 'app/shared/components/app-image/AppImage';
import Button from 'app/shared/components/button/Button';
import CardIconButton from 'app/shared/components/card/CardIconButton';
import {
  EMAIL_RULES,
  maxLengthRule,
  NAME_MAX_LENGTH,
  PW_RULES,
} from 'app/shared/components/form-input/utilities/form-tools';
import IconLib, {
  AppleLogoSvg,
  FacebookLogoSvg,
  GoogleLogoSvg,
} from 'app/shared/components/icon/icon-lib';
import {
  ErrorText,
  InsetAreaSpacer,
  PadCollapse,
  PadExpand,
  TopBannerText,
} from 'app/shared/components/misc';
import Body from 'app/shared/components/typography/body/Body';
import Subheading from 'app/shared/components/typography/subheading/Subheading';
import Form from 'app/shared/frames/Form';
import SharedRoutePath from 'app/shared/SharedRoutePath';
import ClassName from 'app/styles/ClassName';
import styledFuncs from 'app/styles/styledFuncs';
import styledMixins from 'app/styles/styledMixins';
import { CUSTOM_BLUE } from 'app/styles/theme';
import { LSKey } from 'app/tools/hooks/local-storage/local-storage.types';
import useLocalState from 'app/tools/hooks/local-storage/use-local-storage';
import { useEffectOnce } from 'app/tools/hooks/misc-hooks';
import logoBanner from 'assets/logo-banner.svg';
import React, { useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { from } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import styled from 'styled-components/macro';
import queryString from 'query-string';
import FormTextInput from '../components/form-input/FormTextInput';
import useInputForm from '../components/form-input/utilities/use-input-form';
import Icon from '../components/icon/Icon';

const ReferralBox = styled.div.attrs({
  className: ClassName.NO_HEIGHT_MARGIN,
})`
  display: flex;
  justify-content: space-around;
  margin: ${styledFuncs.pad()} auto;
  padding: ${styledFuncs.pad(0.75)} ${styledFuncs.pad()};
  ${styledMixins.roundCorners}
  background: ${CUSTOM_BLUE};
  > * {
    color: white;
    align-self: center;
    &:first-child {
      margin-right: ${styledFuncs.pad()};
    }
  }
`;

type FormValues = {
  firstName: string;
  email: string;
  password1: string;
  password2: string;
  utmSource: string;
};

export default function SignUp(): JSX.Element {
  const auth = useAuthToken();

  const [refStore, setRefStore] = useLocalState(LSKey.REF_CODE, '');
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>();
  const [eventName, setEventName] = useState<string>('');
  const [confirmPasswordError, setConfirmPasswordError] =
    useState<string>('');
  useEffectOnce(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    Device.getInfo().then(devicedetail => {
      setDeviceInfo(devicedetail);
    });
    const eventNameLocal = localStorage.getItem('event');
    if (eventNameLocal) {
      setEventName(eventNameLocal);
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const parsedQuery = queryString.parse(location.search);
    if (parsedQuery) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (parsedQuery.event) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        setEventName(parsedQuery.event as string);
      }
    }
  });
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  initializeFacebookSignIn();
  const {
    request$: register$,
    sending: submitting1,
    error: loginError1,
  } = useRegisterUser();

  const {
    request$: externalAppleSignIn$,
    sending: submitting2,
    error: loginError2,
  } = useAppleExternalSignIn();

  const {
    request$: externalFacebookSignIn$,
    sending: submitting3,
    error: loginError3,
  } = useFacebookExternalSignIn();

  const {
    request$: externalGoogleSignIn$,
    sending: submitting4,
    error: loginError4,
  } = useGoogleExternalSignIn();

  const submitting =
    submitting1 || submitting2 || submitting3 || submitting4;
  const loginError =
    loginError1 || loginError2 || loginError3 || loginError4;

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { handleSubmit, setValue, inputProps } =
    useInputForm<FormValues>({
      mode: 'onTouched',
      apiError: loginError,
    });

  const submit$ = (fieldValues: FormValues) => {
    if (fieldValues.password1 !== fieldValues.password2) {
      setConfirmPasswordError(
        'Please make sure your passwords match.'
      );
      return;
    } else {
      setConfirmPasswordError('');
    }
    if (eventName) {
      fieldValues.utmSource = eventName;
    }
    register$({
      ...fieldValues,
      ...(refStore ? { referralCode: refStore } : {}),
    })
      .pipe(
        tap({
          next: () => setRefStore(''),
          error: () => setValue('password1', ''),
        })
      )
      .subscribe();
  };

  if (auth) return <Redirect to={SharedRoutePath.HOME} />;

  return (
    <>
      {appEnv.APP_VARIANT === 'business' ? (
        <TopBannerText>Business App</TopBannerText>
      ) : (
        <InsetAreaSpacer pos="top" />
      )}
      <PadCollapse pad={appEnv.APP_VARIANT === 'business' ? 5 : 7} />
      <AppImage height="4rem" src={logoBanner} alt="logo banner" />
      <PadCollapse pad={4} />
      <Subheading color="primary" large>
        Let's get started
      </Subheading>
      <Form margin onSubmit={handleSubmit(submit$)}>
        {refStore && (
          <ReferralBox>
            <Body small centered={false}>
              Referral code applied!
            </Body>
            <Button
              iconButton={{ size: 'small' }}
              onClick={() => setRefStore('')}
              css="margin: -5px;"
            >
              <Icon icon={IconLib.CROSS_BOXED} />
            </Button>
          </ReferralBox>
        )}
        <FormTextInput
          controlName="firstName"
          autoComplete="given-name"
          placeholder="Name"
          icon={IconLib.USER}
          classicPlaceholder
          rules={maxLengthRule(NAME_MAX_LENGTH)}
          {...inputProps}
        />
        <FormTextInput
          controlName="email"
          autoComplete="email"
          type="email"
          icon={IconLib.MAIL}
          classicPlaceholder
          rules={EMAIL_RULES}
          {...inputProps}
        />
        <FormTextInput
          controlName="password1"
          placeholder="Password"
          type="password"
          autoComplete="new-password"
          icon={IconLib.LOCK}
          classicPlaceholder
          rules={PW_RULES}
          {...inputProps}
        />
        <FormTextInput
          controlName="password2"
          placeholder="Confirm Password"
          type="password"
          autoComplete="new-password"
          icon={IconLib.LOCK}
          classicPlaceholder
          {...inputProps}
        />
        <ErrorText message={loginError || confirmPasswordError} />
        <Button type="submit" pending={submitting}>
          Sign up
        </Button>
        <Body small centered>
          Already have an account?{' '}
          <Link to={SharedRoutePath.LOGIN}>Sign in</Link>
        </Body>
      </Form>
      <PadCollapse pad={1} />
      <Body tiny centered withNext>
        Or sign in with:
      </Body>
      <div>
        {(deviceInfo?.operatingSystem === 'ios' ||
          deviceInfo?.operatingSystem === 'mac') && (
          <CardIconButton
            imgNode={<AppleLogoSvg />}
            alt="apple"
            color="black"
            onClick={() => {
              from(appleSignIn())
                .pipe(switchMap(externalAppleSignIn$))
                .subscribe();
            }}
          />
        )}
        <CardIconButton
          imgNode={<FacebookLogoSvg />}
          alt="apple"
          color="#1877F2"
          onClick={() => {
            from(facebookSignIn())
              .pipe(switchMap(externalFacebookSignIn$))
              .subscribe();
          }}
        />
        <CardIconButton
          imgNode={<GoogleLogoSvg />}
          alt="apple"
          color="#FFFFFF"
          onClick={() => {
            from(googleSignIn())
              .pipe(switchMap(externalGoogleSignIn$))
              .subscribe();
          }}
        />
      </div>
      <PadCollapse pad={2} />
      <PadExpand />
      <div>
        <Body tiny centered>
          By signing up, you agree to our
          <br />
          <Link to={SharedRoutePath.TERMS_Of_USE}>
            Terms of Use
          </Link>{' '}
          and{' '}
          <Link to={SharedRoutePath.PRIVACY_POLICY}>
            Privacy Policy
          </Link>
          .
        </Body>
      </div>
      <PadCollapse pad={1} />
      <InsetAreaSpacer pos="bottom" />
    </>
  );
}
